import { WAVEFORM_SAMPLES } from '../../widget/constants';

interface WaveformGenerationConfig {
  // Range: 0.5 - 3.0 - влияет на общую высоту волны
  amplitude: number;
  // Range: 0.1 - 5.0 - влияет на контрастность (разброс между высокими и низкими пиками)
  dynamicRange: number;
  // Range: 1 - 10 - количество секций с разной высотой
  complexity: number;
}

export const DEFAULT_WAVEFORM_CONFIG: WaveformGenerationConfig = {
  amplitude: 1.0,
  dynamicRange: 3.5,
  complexity: 7
};

// Константы для генерации огибающей (envelope)
const ENVELOPE_MIN_SECTIONS = 4;
const ENVELOPE_SECTIONS_FACTOR = 0.8;
const ENVELOPE_START_MIN = 0.6;
const ENVELOPE_START_RANGE = 0.4;
const ENVELOPE_HIGH_PEAK_THRESHOLD = 0.3;
const ENVELOPE_HIGH_PEAK_MIN = 0.7;
const ENVELOPE_HIGH_PEAK_RANGE = 0.3;
const ENVELOPE_MODERATE_PEAK_MIN = 0.5;
const ENVELOPE_MODERATE_PEAK_RANGE = 0.3;
const ENVELOPE_END_MIN = 0.6;
const ENVELOPE_END_RANGE = 0.4;

// Константы для аудио-текстуры
const TEXTURE_BASE_FREQ_MIN = 2;
const TEXTURE_BASE_FREQ_FACTOR = 2;
const TEXTURE_BASE_AMP = 2.5;
const TEXTURE_VAR_FREQ_MIN = 8;
const TEXTURE_VAR_FREQ_FACTOR = 6;
const TEXTURE_VAR_AMP = 1.5;
const TEXTURE_DETAIL_FREQ_MIN = 20;
const TEXTURE_DETAIL_FREQ_FACTOR = 15;
const TEXTURE_DETAIL_AMP = 0.8;
const TEXTURE_NOISE_AMP = 1.0;
const TEXTURE_BASE_OFFSET = 0.5;

// Константы для масштабирования
const SCALE_MIN = 0.5;
const SCALE_MAX = 2.0;
const SCALE_FACTOR = 0.4;
const AMPLITUDE_MULTIPLIER = 1.5;

// Linear Congruential Generator (LCG) constants for deterministic pseudo-random numbers
const LCG_MULTIPLIER = 9301;
const LCG_INCREMENT = 49297;
const LCG_MODULUS = 233280;

// Smoothing parameters
const SMOOTH_MIN_PASSES = 0;
const SMOOTH_PASSES_FACTOR = 5;

// Base64 encoding
const BYTE_MAX_VALUE = 255;

const seededRandom = (seed: number) => {
  let currentSeed = seed % LCG_MODULUS;

  return () => {
    currentSeed = (currentSeed * LCG_MULTIPLIER + LCG_INCREMENT) % LCG_MODULUS;
    return currentSeed / LCG_MODULUS;
  };
};

const generateEnvelope = (
  length: number,
  complexity: number,
  random: () => number
) => {
  const envelope = new Float32Array(length);
  const sections = Math.max(
    ENVELOPE_MIN_SECTIONS,
    Math.floor(complexity * ENVELOPE_SECTIONS_FACTOR)
  );

  const controlPoints: { pos: number; amp: number }[] = [];
  controlPoints.push({
    pos: 0,
    amp: ENVELOPE_START_MIN + random() * ENVELOPE_START_RANGE
  });

  for (let i = 0; i < sections; i++) {
    const isHighPeak = random() > ENVELOPE_HIGH_PEAK_THRESHOLD;
    controlPoints.push({
      pos: (i + 1) / (sections + 1),
      amp: isHighPeak
        ? ENVELOPE_HIGH_PEAK_MIN + random() * ENVELOPE_HIGH_PEAK_RANGE
        : ENVELOPE_MODERATE_PEAK_MIN + random() * ENVELOPE_MODERATE_PEAK_RANGE
    });
  }

  controlPoints.push({
    pos: 1,
    amp: ENVELOPE_END_MIN + random() * ENVELOPE_END_RANGE
  });

  for (let i = 0; i < length; i++) {
    const t = i / length;

    let leftPoint = controlPoints[0];
    let rightPoint = controlPoints[controlPoints.length - 1];

    for (let j = 0; j < controlPoints.length - 1; j++) {
      if (t >= controlPoints[j].pos && t <= controlPoints[j + 1].pos) {
        leftPoint = controlPoints[j];
        rightPoint = controlPoints[j + 1];
        break;
      }
    }

    const segmentProgress =
      (t - leftPoint.pos) / (rightPoint.pos - leftPoint.pos);
    const smoothProgress = (1 - Math.cos(segmentProgress * Math.PI)) / 2;

    envelope[i] =
      leftPoint.amp + (rightPoint.amp - leftPoint.amp) * smoothProgress;
  }

  return envelope;
};

const generateAudioTexture = (
  peaks: Float32Array,
  length: number,
  random: () => number
) => {
  for (let i = 0; i < length; i++) {
    const t = i / length;
    let texture = 0;

    const baseFreq =
      TEXTURE_BASE_FREQ_MIN + random() * TEXTURE_BASE_FREQ_FACTOR;
    texture +=
      Math.sin(t * Math.PI * 2 * baseFreq + random() * Math.PI) *
      TEXTURE_BASE_AMP;

    const varFreq = TEXTURE_VAR_FREQ_MIN + random() * TEXTURE_VAR_FREQ_FACTOR;
    texture +=
      Math.sin(t * Math.PI * 2 * varFreq + random() * Math.PI) *
      TEXTURE_VAR_AMP;

    const detailFreq =
      TEXTURE_DETAIL_FREQ_MIN + random() * TEXTURE_DETAIL_FREQ_FACTOR;
    texture +=
      Math.sin(t * Math.PI * 2 * detailFreq + random() * Math.PI) *
      TEXTURE_DETAIL_AMP;

    texture += (random() - 0.5) * TEXTURE_NOISE_AMP;

    peaks[i] = Math.abs(texture) + TEXTURE_BASE_OFFSET;
  }
};

const applyEnvelope = (
  peaks: Float32Array,
  envelope: Float32Array,
  length: number
) => {
  for (let i = 0; i < length; i++) {
    peaks[i] = peaks[i] * envelope[i];
  }
};

const applySmoothing = (
  peaks: Float32Array,
  length: number,
  smoothness: number
) => {
  if (smoothness === 0) {
    return;
  }

  const temp = new Float32Array(length);
  const smoothPasses = Math.max(
    SMOOTH_MIN_PASSES,
    Math.floor(smoothness * SMOOTH_PASSES_FACTOR)
  );

  const getWeight = (distance: number) => {
    return Math.exp(-(distance * distance) / (2 * smoothness));
  };

  for (let pass = 0; pass < smoothPasses; pass++) {
    for (let i = 0; i < length; i++) {
      const prev2 = i >= 2 ? peaks[i - 2] : peaks[i];
      const prev1 = i >= 1 ? peaks[i - 1] : peaks[i];
      const curr = peaks[i];
      const next1 = i < length - 1 ? peaks[i + 1] : peaks[i];
      const next2 = i < length - 2 ? peaks[i + 2] : peaks[i];

      const w2 = getWeight(2);
      const w1 = getWeight(1);
      const w0 = getWeight(0);
      const totalWeight = w0 + 2 * (w1 + w2);

      temp[i] =
        (prev2 * w2 + prev1 * w1 + curr * w0 + next1 * w1 + next2 * w2) /
        totalWeight;
    }

    for (let i = 0; i < length; i++) {
      peaks[i] = temp[i];
    }
  }
};

const normalizePeaks = (peaks: Float32Array, length: number) => {
  let min = Infinity;
  let max = -Infinity;

  for (let i = 0; i < length; i++) {
    min = Math.min(min, peaks[i]);
    max = Math.max(max, peaks[i]);
  }

  const range = max - min;

  for (let i = 0; i < length; i++) {
    peaks[i] = (peaks[i] - min) / range;
  }
};
const applyCompression = (
  peaks: Float32Array,
  length: number,
  dynamicRange: number
) => {
  const scale = Math.max(
    SCALE_MIN,
    Math.min(SCALE_MAX, dynamicRange * SCALE_FACTOR)
  );

  for (let i = 0; i < length; i++) {
    peaks[i] = peaks[i] * scale;
  }
};

const applyAmplitudeAndClamp = (
  peaks: Float32Array,
  length: number,
  amplitude: number
) => {
  for (let i = 0; i < length; i++) {
    peaks[i] = peaks[i] * amplitude * AMPLITUDE_MULTIPLIER;
    peaks[i] = Math.max(0, Math.min(1, peaks[i]));
  }
};

const encodeToBase64 = (peaks: Float32Array, length: number) => {
  const bytes = new Uint8Array(length);

  for (let i = 0; i < length; i++) {
    bytes[i] = Math.round(peaks[i] * BYTE_MAX_VALUE);
  }

  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary);
};

export const getRandomWaveform = (
  length = WAVEFORM_SAMPLES,
  seed?: number,
  config: Partial<WaveformGenerationConfig> = {}
) => {
  const cfg = { ...DEFAULT_WAVEFORM_CONFIG, ...config };
  const random = seed !== undefined ? seededRandom(seed) : Math.random;
  const peaks = new Float32Array(length);

  const envelope = generateEnvelope(length, cfg.complexity, random);

  generateAudioTexture(peaks, length, random);
  applyEnvelope(peaks, envelope, length);
  applySmoothing(peaks, length, 0);
  normalizePeaks(peaks, length);
  applyCompression(peaks, length, cfg.dynamicRange);
  applyAmplitudeAndClamp(peaks, length, cfg.amplitude);

  return encodeToBase64(peaks, length);
};
